/**
 * Format large numbers with K, M, B abbreviations
 */
export const formatCurrency = (value: number | undefined | null): string => {
  if (value === undefined || value === null || isNaN(value)) return '$0';
  
  // Sanity check for data errors (trillion+ is definitely wrong)
  if (Math.abs(value) > 1000000000000) return '$0';
  
  const absValue = Math.abs(value);
  
  if (absValue >= 1000000000) {
    return `$${(value / 1000000000).toFixed(1)}B`;
  }
  if (absValue >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  if (absValue >= 10000) {
    return `$${(value / 1000).toFixed(1)}K`;
  }
  if (absValue >= 1000) {
    return `$${value.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  }
  return `$${value.toFixed(2)}`;
};

/**
 * Format number with K, M, B abbreviations (no currency symbol)
 */
export const formatNumber = (value: number | undefined | null): string => {
  if (value === undefined || value === null || isNaN(value)) return '0';
  
  const absValue = Math.abs(value);
  
  if (absValue >= 1000000000) {
    return `${(value / 1000000000).toFixed(1)}B`;
  }
  if (absValue >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  }
  if (absValue >= 10000) {
    return `${(value / 1000).toFixed(1)}K`;
  }
  if (absValue >= 1000) {
    return value.toLocaleString('en-US', { maximumFractionDigits: 0 });
  }
  return value.toFixed(0);
};

/**
 * Format percentage change with reasonable limits
 */
export const formatPercentage = (value: number | undefined | null): string => {
  if (value === undefined || value === null || isNaN(value)) return '0%';
  
  // Cap percentages at reasonable values
  const cappedValue = Math.max(-999, Math.min(999, value));
  
  if (Math.abs(cappedValue) < 0.01) return '0%';
  if (Math.abs(cappedValue) >= 100) {
    return `${cappedValue > 0 ? '+' : ''}${cappedValue.toFixed(0)}%`;
  }
  return `${cappedValue > 0 ? '+' : ''}${cappedValue.toFixed(1)}%`;
};

/**
 * Format average order value
 */
export const formatAverage = (total: number, count: number): string => {
  if (!count || count === 0 || isNaN(total) || isNaN(count)) return '$0';
  
  const avg = total / count;
  
  // Sanity check - average order shouldn't be more than $1000
  if (avg > 1000) return '$0';
  
  return `$${avg.toFixed(2)}`;
};